import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Truck, User } from "lucide-react";

export default function Dispatch() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["dispatch-board"], queryFn: () => api.get("/dispatch/board").then((r) => r.data),
    refetchInterval: 10000,
  });
  const [assignOrder, setAssignOrder] = useState(null);
  const [bowser, setBowser] = useState(""); const [driver, setDriver] = useState("");

  const dispatchMut = useMutation({
    mutationFn: ({ id, bowser_id, driver_id }) => api.post(`/orders/${id}/dispatch`, { bowser_id, driver_id }),
    onSuccess: () => { toast.success("Dispatched"); qc.invalidateQueries({ queryKey: ["dispatch-board"] }); setAssignOrder(null); setBowser(""); setDriver(""); },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed"),
  });

  if (isLoading) return <div>Loading...</div>;

  return (
    <div className="space-y-6" data-testid="dispatch-board">
      <div>
        <h1 className="font-display text-3xl font-black tracking-tight">Dispatch Control</h1>
        <p className="text-sm text-muted-foreground">Assign bowsers and drivers to pending orders.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle>Pending orders ({data?.pending?.length || 0})</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {(data?.pending || []).map((o) => (
              <div key={o.id} className="flex items-center justify-between rounded-lg border border-border/60 p-3 transition-transform hover:-translate-y-0.5">
                <div>
                  <Link to={`/app/orders/${o.id}`} className="font-mono text-sm font-bold hover:text-primary" data-testid={`dispatch-pending-${o.code}`}>{o.code}</Link>
                  <div className="text-xs text-muted-foreground">{o.customer_name} · {o.quantity_litres}L · {o.site_name || "—"}</div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={o.status} />
                  <Button size="sm" onClick={() => setAssignOrder(o)} data-testid={`btn-assign-${o.code}`}>Assign</Button>
                </div>
              </div>
            ))}
            {(!data?.pending || data.pending.length === 0) && <div className="py-6 text-center text-sm text-muted-foreground">No pending orders</div>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Active trips ({data?.active?.length || 0})</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {(data?.active || []).map((o) => (
              <div key={o.id} className="flex items-center justify-between rounded-lg border border-border/60 p-3">
                <div>
                  <Link to={`/app/orders/${o.id}`} className="font-mono text-sm font-bold hover:text-primary">{o.code}</Link>
                  <div className="text-xs text-muted-foreground">{o.customer_name} · {o.quantity_litres}L</div>
                  <div className="mt-1 flex items-center gap-2 text-xs">
                    <Truck size={12} /> {o.bowser_code} · <User size={12} /> {o.driver_name}
                  </div>
                </div>
                <StatusBadge status={o.status} />
              </div>
            ))}
            {(!data?.active || data.active.length === 0) && <div className="py-6 text-center text-sm text-muted-foreground">No active trips</div>}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle>Fleet · Bowsers</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {(data?.bowsers || []).map((b) => (
              <div key={b.id} className="rounded-lg border border-border/60 p-3">
                <div className="flex items-center justify-between">
                  <div className="font-mono text-sm font-bold">{b.code}</div>
                  <StatusBadge status={b.status} />
                </div>
                <div className="mt-1 text-xs text-muted-foreground">{b.registration_no}</div>
                <div className="mt-2 text-xs">Capacity: <b>{b.capacity_litres}L</b> · Stock: <b>{Math.round(b.current_stock || 0)}L</b></div>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Fleet · Drivers</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {(data?.drivers || []).map((d) => (
              <div key={d.id} className="rounded-lg border border-border/60 p-3">
                <div className="flex items-center justify-between">
                  <div className="font-mono text-sm font-bold">{d.code}</div>
                  <StatusBadge status={d.status} />
                </div>
                <div className="mt-1 text-xs">{d.name}</div>
                <div className="text-xs text-muted-foreground">{d.phone}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!assignOrder} onOpenChange={(v) => !v && setAssignOrder(null)}>
        <DialogContent data-testid="dispatch-assign-dialog">
          <DialogHeader><DialogTitle>Assign {assignOrder?.code}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Bowser</Label>
              <Select value={bowser} onValueChange={setBowser}>
                <SelectTrigger data-testid="dispatch-select-bowser"><SelectValue placeholder="Select bowser" /></SelectTrigger>
                <SelectContent>
                  {(data?.bowsers || []).filter((b) => b.status === "available").map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.code} · {b.capacity_litres}L</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Driver</Label>
              <Select value={driver} onValueChange={setDriver}>
                <SelectTrigger data-testid="dispatch-select-driver"><SelectValue placeholder="Select driver" /></SelectTrigger>
                <SelectContent>
                  {(data?.drivers || []).filter((d) => d.status === "available").map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.code} · {d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => dispatchMut.mutate({ id: assignOrder.id, bowser_id: bowser, driver_id: driver })}
              disabled={!bowser || !driver || dispatchMut.isPending}
              data-testid="dispatch-confirm-btn"
            >Dispatch</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
